
import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { toast } from "sonner"
import AdminLayout from "@/layouts/AdminLayout"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { supabase } from "@/integrations/supabase/client"
import { subscriptionTiers } from "@/data/subscriptionTiers"
import { CategoryFormNew } from "@/components/categories/CategoryFormNew"
import { FieldFormNew } from "@/components/categories/FieldFormNew"
import { CategoryList } from "@/components/categories/CategoryList"
import type { Database } from "@/integrations/supabase/types"

type SubscriptionTier = "essentials" | "pro" | "elite"
type Category = Database["public"]["Tables"]["client_categories"]["Row"]
type Field = Database["public"]["Tables"]["category_fields"]["Row"]
type FieldType = Database["public"]["Enums"]["field_type"]

export default function ManageFields() {
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier>("essentials")

  const { data: categories, refetch: refetchCategories } = useQuery({
    queryKey: ["categories", selectedTier],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client_categories")
        .select("*")
        .eq("subscription_tier", selectedTier)
        .eq("active", true)
        .order("name")

      if (error) {
        toast.error("Error loading categories")
        return []
      }
      return data as Category[]
    }
  })

  const { data: fields, refetch: refetchFields } = useQuery({
    queryKey: ["fields", categories],
    queryFn: async () => {
      if (!categories?.length) return []
      
      const { data, error } = await supabase
        .from("category_fields")
        .select("*")
        .in(
          "category_id",
          categories.map((c) => c.id)
        )

      if (error) {
        toast.error("Error loading fields")
        return []
      }
      return data as Field[]
    },
    enabled: !!categories?.length
  })

  const handleAddCategory = async (name: string, description: string) => {
    if (!name.trim()) {
      toast.error("Category name is required")
      return
    }

    const { error } = await supabase.from("client_categories").insert({
      name,
      description: description || null,
      subscription_tier: selectedTier,
    })

    if (error) {
      toast.error("Error creating category")
      return
    }

    toast.success("Category created successfully")
    refetchCategories()
  }

  const handleAddField = async (newField: {
    categoryId: string
    name: string
    type: string
    required: boolean
    options: string
  }) => {
    if (!newField.categoryId || !newField.name.trim()) {
      toast.error("Category and field name are required")
      return
    }

    // Validate field type
    const fieldType = newField.type as FieldType
    if (!["text", "textarea", "radio", "select", "date"].includes(fieldType)) {
      toast.error("Invalid field type")
      return
    }

    const needsOptions = ["radio", "select"].includes(fieldType)
    if (needsOptions && !newField.options.trim()) {
      toast.error("Options are required for radio and select fields")
      return
    }

    const { error } = await supabase.from("category_fields").insert({
      category_id: newField.categoryId,
      field_name: newField.name,
      field_type: fieldType,
      required: newField.required,
      options: needsOptions
        ? newField.options.split(",").map(o => o.trim())
        : null,
    })

    if (error) {
      toast.error("Error creating field")
      return
    }

    toast.success("Field created successfully")
    refetchFields()
  }

  const handleDeleteCategory = async (categoryId: string) => {
    const { error } = await supabase
      .from("client_categories")
      .update({ active: false })
      .eq("id", categoryId)

    if (error) {
      toast.error("Error deleting category")
      return
    }

    toast.success("Category deleted successfully")
    refetchCategories()
  }

  const handleDeleteField = async (fieldId: string) => {
    const { error } = await supabase
      .from("category_fields")
      .delete()
      .eq("id", fieldId)

    if (error) {
      toast.error("Error deleting field")
      return
    }

    toast.success("Field deleted successfully")
    refetchFields()
  }

  return (
    <AdminLayout title="Field Management">
      <Tabs value={selectedTier} onValueChange={(value: SubscriptionTier) => setSelectedTier(value)}>
        <TabsList className="grid w-full grid-cols-3">
          {subscriptionTiers.map((tier) => (
            <TabsTrigger 
              key={tier.name.toLowerCase()} 
              value={tier.name.toLowerCase() as SubscriptionTier}
            >
              {tier.name}
            </TabsTrigger>
          ))}
        </TabsList>

        {subscriptionTiers.map((tier) => (
          <TabsContent 
            key={tier.name.toLowerCase()} 
            value={tier.name.toLowerCase() as SubscriptionTier}
          >
            <div className="space-y-6">
              <CategoryFormNew onSubmit={handleAddCategory} />
              <FieldFormNew 
                categories={categories} 
                onSubmit={handleAddField}
              />
              <CategoryList
                categories={categories}
                fields={fields}
                onDeleteCategory={handleDeleteCategory}
                onDeleteField={handleDeleteField}
              />
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </AdminLayout>
  )
}
